/**
 * Composant GlobalSearchResults
 * Affiche les résultats de la recherche globale dans un popover débordant
 */

import { useNavigate } from 'react-router-dom';
import {
  User,
  Users,
  ShoppingCart,
  Car,
  Building2,
  FileText,
  UserCircle,
  Loader2,
} from 'lucide-react';
import type {
  SearchResult,
  SearchResultType,
  UseGlobalSearchReturn,
} from '@/hooks/useGlobalSearch';
import { ENTITY_TYPE_LABELS } from '@/hooks/useGlobalSearch';
import { Badge } from '@/components/ui/badge';

// =====================================================
// TYPES
// =====================================================

interface GlobalSearchResultsProps {
  searchState: UseGlobalSearchReturn;
  query: string;
  onResultClick: () => void;
}

// =====================================================
// ICÔNES PAR TYPE D'ENTITÉ
// =====================================================

const ENTITY_ICONS: Record<SearchResultType, React.ComponentType<{ className?: string }>> = {
  contact: User,
  crew: Users,
  prospect: UserCircle,
  product: ShoppingCart,
  vehicle: Car,
  school: Building2,
  association: Building2,
  form: FileText,
  user: User,
};

// =====================================================
// COMPOSANTS
// =====================================================

function ResultItem({ result, onClick }: { result: SearchResult; onClick: () => void }) {
  const Icon = ENTITY_ICONS[result.type];

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-zinc-100 transition-colors rounded-md group"
    >
      <div className="flex-shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-zinc-500 group-hover:text-zinc-700" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-zinc-900 truncate" title={result.title}>
          {result.title}
        </div>
        {(result.subtitle || result.status || result.metadata) && (
          <div className="flex items-center gap-2 mt-0.5 text-xs text-zinc-500 truncate">
            {result.subtitle && (
              <span className="truncate" title={result.subtitle}>
                {result.subtitle}
              </span>
            )}
            {result.subtitle && (result.status || result.metadata) && <span>•</span>}
            {result.status ? (
              <Badge
                title={result.status.label}
                className={`max-w-[160px] ${result.status.colors.bg} ${result.status.colors.text} border ${result.status.colors.border}`}
              >
                {result.status.label}
              </Badge>
            ) : (
              result.metadata && (
                <span className="truncate" title={result.metadata}>
                  {result.metadata}
                </span>
              )
            )}
          </div>
        )}
      </div>
    </button>
  );
}

function ResultSection({
  type,
  results,
  onResultClick,
}: {
  type: SearchResultType;
  results: SearchResult[];
  onResultClick: (url: string) => void;
}) {
  if (results.length === 0) return null;

  return (
    <div className="mb-4 last:mb-0">
      <div className="px-3 py-1.5 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
        {ENTITY_TYPE_LABELS[type]}
      </div>
      <div className="space-y-0.5">
        {results.map((result) => (
          <ResultItem key={result.id} result={result} onClick={() => onResultClick(result.url)} />
        ))}
      </div>
    </div>
  );
}

// =====================================================
// COMPOSANT PRINCIPAL
// =====================================================

export function GlobalSearchResults({
  searchState,
  query,
  onResultClick,
}: GlobalSearchResultsProps) {
  const navigate = useNavigate();

  const handleResultClick = (url: string) => {
    navigate(url);
    onResultClick();
  };

  // Pas de résultats à afficher si la query est trop courte
  if (query.length < 2) {
    return (
      <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-zinc-200 rounded-lg shadow-xl z-50 w-[480px]">
        <div className="p-4 text-sm text-zinc-500 text-center">
          Tapez au moins 2 caractères pour rechercher
        </div>
      </div>
    );
  }

  // État de chargement
  if (searchState.loading) {
    return (
      <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-zinc-200 rounded-lg shadow-xl z-50 w-[480px]">
        <div className="p-4 flex items-center justify-center gap-2 text-sm text-zinc-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Recherche en cours...</span>
        </div>
      </div>
    );
  }

  // Aucun résultat
  if (!searchState.hasResults) {
    return (
      <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-zinc-200 rounded-lg shadow-xl z-50 w-[480px]">
        <div className="p-4 text-sm text-zinc-500 text-center">
          Aucun résultat pour &quot;{query}&quot;
        </div>
      </div>
    );
  }

  // Affichage des résultats groupés par type
  const sections: Array<{
    type: SearchResultType;
    results: SearchResult[];
  }> = [
    { type: 'contact', results: searchState.results.contacts },
    { type: 'crew', results: searchState.results.crews },
    { type: 'prospect', results: searchState.results.prospects },
    { type: 'product', results: searchState.results.products },
    { type: 'vehicle', results: searchState.results.vehicles },
    { type: 'school', results: searchState.results.schools },
    { type: 'association', results: searchState.results.associations },
    { type: 'form', results: searchState.results.forms },
    { type: 'user', results: searchState.results.users },
  ];

  return (
    <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-zinc-200 rounded-lg shadow-xl z-50 w-[480px] max-h-[70vh] overflow-y-auto">
      <div className="py-2">
        {sections.map((section) => (
          <ResultSection
            key={section.type}
            type={section.type}
            results={section.results}
            onResultClick={handleResultClick}
          />
        ))}
      </div>

      {/* Footer avec le nombre total de résultats */}
      {searchState.totalCount > 0 && (
        <div className="border-t border-zinc-200 px-3 py-2 text-xs text-zinc-500 text-center">
          {searchState.totalCount} résultat{searchState.totalCount > 1 ? 's' : ''} trouvé
          {searchState.totalCount > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
